import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, RefreshCw, Users, AlertTriangle, DollarSign, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeaveReports() {
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeReport, setActiveReport] = useState("annual");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [reportData, setReportData] = useState(null);
  const { businessId } = useBusiness();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then(res => res.ok ? res.json() : null)
      .then(setCurrentUser);
  }, []);

  // Reset report data when business changes
  useEffect(() => {
    setReportData(null);
  }, [businessId]);

  const fetchReport = async (type) => {
    setLoading(true);
    const businessQuery = businessId ? `&business_id=${businessId}` : "";
    const urls = {
      annual: `${BACKEND_URL}/api/leave/reports/annual-summary?year=${year}${businessQuery}`,
      employee: `${BACKEND_URL}/api/leave/reports/employee-balance?year=${year}${businessQuery}`,
      lwp: `${BACKEND_URL}/api/leave/reports/lwp?year=${year}&month=${month}${businessQuery}`,
      salary: `${BACKEND_URL}/api/leave/reports/salary-deduction?year=${year}&month=${month}${businessQuery}`
    };
    
    try {
      const res = await fetch(urls[type], { credentials: "include" });
      if (res.ok) {
        setReportData(await res.json());
        toast.success("Report generated");
      }
    } catch (e) {
      toast.error("Error generating report");
    } finally {
      setLoading(false);
    }
  };

  const isManager = currentUser?.role && ["super_admin", "admin", "manager"].includes(currentUser.role);
  if (!isManager) return <div className="text-center py-12"><AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" /><p>Manager access required</p></div>;

  const reports = [
    { id: "annual", label: "Annual Summary", icon: BarChart3 },
    { id: "employee", label: "Employee Balance", icon: Users },
    { id: "lwp", label: "LWP Report", icon: AlertTriangle },
    { id: "salary", label: "Salary Deduction", icon: DollarSign }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Leave Reports</h1>
          <p className="text-slate-500">Generate leave and deduction reports</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {reports.map(r => (
          <Card key={r.id} className={`cursor-pointer ${activeReport === r.id ? "ring-2 ring-blue-500" : ""}`} onClick={() => setActiveReport(r.id)}>
            <CardContent className="p-4 text-center">
              <r.icon className="w-8 h-8 mx-auto mb-2" />
              <p className="font-medium text-sm">{r.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-4 items-end">
          <div className="space-y-2">
            <Label>Year</Label>
            <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>{[2024,2025,2026,2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {(activeReport === "lwp" || activeReport === "salary") && (
            <div className="space-y-2">
              <Label>Month</Label>
              <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m,i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={() => fetchReport(activeReport)} disabled={loading}>
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}Generate
          </Button>
        </CardContent>
      </Card>

      {reportData && activeReport === "annual" && (
        <Card>
          <CardHeader><CardTitle>Annual Summary - {reportData.year}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="p-4 bg-blue-50 rounded"><p className="text-sm text-blue-600">Total</p><p className="text-2xl font-bold">{reportData.requests_summary?.total}</p></div>
              <div className="p-4 bg-emerald-50 rounded"><p className="text-sm text-emerald-600">Approved</p><p className="text-2xl font-bold">{reportData.requests_summary?.approved}</p></div>
              <div className="p-4 bg-red-50 rounded"><p className="text-sm text-red-600">Rejected</p><p className="text-2xl font-bold">{reportData.requests_summary?.rejected}</p></div>
              <div className="p-4 bg-amber-50 rounded"><p className="text-sm text-amber-600">Pending</p><p className="text-2xl font-bold">{reportData.requests_summary?.pending}</p></div>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Type</TableHead><TableHead className="text-right">Allocated</TableHead><TableHead className="text-right">Used</TableHead><TableHead className="text-right">Available</TableHead></TableRow></TableHeader>
              <TableBody>{reportData.leave_types_summary?.map(t => <TableRow key={t.code}><TableCell><Badge variant="outline">{t.code}</Badge> {t.name}</TableCell><TableCell className="text-right">{t.total_allocated}</TableCell><TableCell className="text-right text-blue-600">{t.total_used}</TableCell><TableCell className="text-right text-emerald-600">{t.total_available}</TableCell></TableRow>)}</TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {reportData && activeReport === "employee" && (
        <Card>
          <CardHeader><CardTitle>Employee Balance - {reportData.year}</CardTitle><CardDescription>{reportData.staff_count} employees</CardDescription></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Allocated</TableHead><TableHead className="text-right">Used</TableHead><TableHead className="text-right">Available</TableHead></TableRow></TableHeader>
              <TableBody>{reportData.staff_balances?.map(s => <TableRow key={s.user_id}><TableCell><p className="font-medium">{s.name}</p><p className="text-xs text-slate-500">{s.email}</p></TableCell><TableCell><Badge variant="outline">{s.role}</Badge></TableCell><TableCell className="text-right">{s.summary?.total_allocated}</TableCell><TableCell className="text-right text-blue-600">{s.summary?.total_used}</TableCell><TableCell className="text-right font-bold text-emerald-600">{s.summary?.total_available}</TableCell></TableRow>)}</TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {reportData && activeReport === "lwp" && (
        <Card>
          <CardHeader><CardTitle>LWP Report</CardTitle><CardDescription>Total: {reportData.total_lwp_days} days | Deduction: ₹{reportData.total_deduction?.toLocaleString()}</CardDescription></CardHeader>
          <CardContent>
            {reportData.employees?.length === 0 ? <p className="text-center py-8 text-slate-500">No LWP records</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead className="text-right">LWP Days</TableHead><TableHead className="text-right">Basic</TableHead><TableHead className="text-right">Deduction</TableHead></TableRow></TableHeader>
                <TableBody>{reportData.employees?.map(e => <TableRow key={e.user_id}><TableCell>{e.user_name}</TableCell><TableCell className="text-right text-amber-600">{e.lwp_days}</TableCell><TableCell className="text-right">₹{e.basic_salary?.toLocaleString()}</TableCell><TableCell className="text-right text-red-600 font-bold">₹{e.total_deduction?.toLocaleString()}</TableCell></TableRow>)}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {reportData && activeReport === "salary" && (
        <Card>
          <CardHeader><CardTitle>Salary Deduction Report</CardTitle><CardDescription>LWP: {reportData.summary?.total_lwp_days} days | Total: ₹{reportData.summary?.total_lwp_deduction?.toLocaleString()}</CardDescription></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead className="text-right">Paid Leaves</TableHead><TableHead className="text-right">LWP</TableHead><TableHead className="text-right">Deduction</TableHead></TableRow></TableHeader>
              <TableBody>{reportData.employees?.map(e => <TableRow key={e.user_id}><TableCell>{e.user_name}</TableCell><TableCell className="text-right text-emerald-600">{e.paid_leaves}</TableCell><TableCell className="text-right text-amber-600">{e.lwp_days}</TableCell><TableCell className="text-right text-red-600 font-bold">₹{e.lwp_deduction?.toLocaleString()}</TableCell></TableRow>)}</TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
